// features/profile/edit/EditProfilePage.tsx — /settings/profile (1.27.70).
//
// The full Edit Profile page from the owner's edit_profile_v2 concept; it replaces
// the old EditProfileDialog. One form, saved with one PATCH of only what changed
// (see useProfileDraft). Leaving with unsaved changes asks first. Owner rulings
// 2026-09-30: the route is /settings/profile; pronouns, creator type, tagline,
// birthday and links are real fields; header style, background effects and the
// two discoverability switches are Coming soon.
import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Link, Navigate, useBlocker, useLocation } from 'react-router-dom';
import { Button, Dialog, useToast } from '@/ui';
import { Icon } from '@/icons';
import { usePageTitle, useSession } from '@/hooks';
import { media } from '@/theme/media';
import { mediaService, uploadErrorMessage } from '@/core/services/mediaService';
import { hostnameOf, isWebAddress, normalizeUrl, shownBirthday } from '@/core/lib/profileFields';
import type { User } from '@/core/models/account';
import { ConfirmDialog } from '@/components/ConfirmDialog/ConfirmDialog';
import { SettingsHeader } from '@/features/settings/SettingsHeader';
import { ConfirmDropFollowersDialog } from '@/features/settings/sections/ConfirmDropFollowersDialog';
import { EditProfileSidebar } from './EditProfileSidebar';
import { EDIT_SECTIONS } from './editSections';
import {
  useProfileDraft,
  validateDraft,
  type DraftErrors,
  type ProfileDraftState,
} from './useProfileDraft';
import { AboutIdentityCard, BasicInfoCard, PhotoBannerCard } from './IdentityCards';
import { LinksCard } from './LinksCard';
import {
  AppearanceCard,
  InterestsCard,
  MottoCard,
  PreviewCard,
  PrivacyCard,
  VerificationCard,
} from './MoreCards';
import { ProfilePreview } from './ProfilePreview';

// ─── Shell (mirrors SettingsLayout) ──────────────────────────────────────────

const Page = styled.div`
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.color.bgRoot};
`;

const Body = styled.div`
  display: grid;
  grid-template-columns: 280px minmax(0, 1fr);
  flex: 1 0 auto;
  @media (min-width: 768px) and (max-width: 1023.98px) {
    grid-template-columns: 230px minmax(0, 1fr);
  }
  ${media.mobile`grid-template-columns: minmax(0, 1fr);`}
`;

const SidebarWrap = styled.div`
  min-width: 0;
  position: sticky;
  top: 78px;
  height: calc(100dvh - 78px);
  align-self: start;
  overflow-y: auto;
  scrollbar-width: thin;
  padding: 32px 24px;
  background: ${({ theme }) => theme.color.surfaceChrome};
  border-right: 1px solid ${({ theme }) => theme.color.glassBorder};
  @media (max-width: 1023.98px) {
    padding: 32px 16px;
  }
  ${media.mobile`display: none;`}
`;

const Main = styled.main`
  min-width: 0;
  width: 100%;
  max-width: 1440px;
  padding: 32px 36px 64px;
  ${media.tabletDown`padding: 32px 24px 56px;`}
  ${media.mobile`padding: 20px 16px 48px;`}
`;

const MobileNav = styled.nav`
  display: none;
  ${media.mobile`
    display: flex;
    gap: 8px;
    overflow-x: auto;
    margin: 0 -16px 16px;
    padding: 0 16px 4px;
    scrollbar-width: none;
  `}
  > a {
    flex: none;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 6px 12px;
    border-radius: ${({ theme }) => theme.radius.pill};
    background: ${({ theme }) => theme.color.surface};
    color: ${({ theme }) => theme.color.textMuted};
    font-size: ${({ theme }) => theme.fontSize.xs};
    text-decoration: none;
  }
  > a[aria-current] {
    color: ${({ theme }) => theme.color.textPrimary};
    background: color-mix(in srgb, ${({ theme }) => theme.color.accentCyan} 16%, transparent);
  }
`;

// ─── Page head ───────────────────────────────────────────────────────────────

const Head = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px 24px;
  margin-bottom: 24px;
`;

const H1 = styled.h2`
  margin: 0;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.xl};
  line-height: ${({ theme }) => theme.lineHeight.xl};
  font-weight: 600;
  color: ${({ theme }) => theme.color.textPrimary};
`;

const Lede = styled.p`
  margin: 4px 0 0;
  font-size: ${({ theme }) => theme.fontSize.sm};
  line-height: ${({ theme }) => theme.lineHeight.sm};
  color: ${({ theme }) => theme.color.textMuted};
`;

const HeadActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const Unsaved = styled.span`
  align-self: center;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

// ─── Card grids (sized by their own width, like the Settings dashboard) ─────

const Grids = styled.div`
  container-type: inline-size;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const TopGrid = styled.div`
  display: grid;
  gap: 16px;
  /* The concept's proportions: photo column widest, preview narrowest. */
  grid-template-columns: minmax(0, 1.15fr) minmax(0, 1fr) minmax(0, 0.95fr);
  grid-template-areas:
    'photo basic preview'
    'about basic preview';
  align-items: start;
  > [data-testid='photo-card'] {
    grid-area: photo;
  }
  > [data-testid='about-card'] {
    grid-area: about;
  }
  > [data-testid='basic-card'] {
    grid-area: basic;
    align-self: stretch;
  }
  > [data-testid='preview-card'] {
    grid-area: preview;
    position: sticky;
    top: 94px;
  }
  @container (max-width: 1039.98px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    grid-template-areas:
      'photo basic'
      'about basic'
      'preview preview';
    > [data-testid='preview-card'] {
      position: static;
    }
  }
  @container (max-width: 719.98px) {
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas: 'photo' 'basic' 'about' 'preview';
  }
`;

const BottomGrid = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: minmax(0, 1.1fr) minmax(0, 1fr) minmax(0, 1.1fr) minmax(0, 0.75fr);
  grid-template-areas:
    'links appearance privacy motto'
    'links interests verification motto';
  > [data-testid='links-card'] {
    grid-area: links;
  }
  > [data-testid='appearance-card'] {
    grid-area: appearance;
  }
  > [data-testid='privacy-card'] {
    grid-area: privacy;
  }
  > [data-testid='interests-card'] {
    grid-area: interests;
  }
  > [data-testid='verification-card'] {
    grid-area: verification;
  }
  > [data-testid='motto-card'] {
    grid-area: motto;
  }
  @container (max-width: 1039.98px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    grid-template-areas:
      'links appearance'
      'links privacy'
      'interests verification';
    > [data-testid='motto-card'] {
      display: none;
    }
  }
  @container (max-width: 719.98px) {
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas: 'links' 'appearance' 'privacy' 'interests' 'verification';
  }
`;

// ─── Preview user ────────────────────────────────────────────────────────────

function previewOf(user: User, state: ProfileDraftState): User {
  const { draft } = state;
  const website = normalizeUrl(draft.website);
  return {
    ...user,
    name: draft.displayName.trim() || user.name,
    bio: draft.bio,
    pronouns: draft.pronouns.trim(),
    creatorType: draft.creatorType.trim(),
    tagline: draft.tagline.trim(),
    location: draft.location.trim(),
    link: website && isWebAddress(website) ? { label: hostnameOf(website), href: website } : null,
    links: draft.links,
    avatarKey: state.avatarShown,
    banner: state.bannerShown,
    bannerTone: draft.bannerTone,
    privateAccount: draft.privateAccount,
    birthdayPublic: shownBirthday(draft.birthday, draft.birthdayVisibility),
    invitedBy: null,
  };
}

// ─── The form ────────────────────────────────────────────────────────────────

function EditProfileForm({ user, onSaved }: { user: User; onSaved: () => void }) {
  const { updateProfile } = useSession();
  const toast = useToast();
  const state = useProfileDraft(user);
  const { draft, dirty, changes, media: mediaDraft, reset } = state;
  const [errors, setErrors] = useState<DraftErrors>({});
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [confirmDrop, setConfirmDrop] = useState(false);
  const allowLeave = useRef(false);

  const blocker = useBlocker(() => dirty && !allowLeave.current);
  useEffect(() => {
    if (!dirty) return;
    const guard = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', guard);
    return () => window.removeEventListener('beforeunload', guard);
  }, [dirty]);

  const previewUser = previewOf(user, state);

  async function save(dropConfirmed = false) {
    const found = validateDraft(draft);
    setErrors(found);
    const firstBad = Object.keys(found)[0];
    if (firstBad) {
      const card = {
        displayName: 'basic',
        bio: 'basic',
        pronouns: 'basic',
        creatorType: 'basic',
        links: 'links',
      }[firstBad];
      document
        .getElementById(card ?? 'about')
        ?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
      toast('Check the highlighted fields');
      return;
    }
    if (!dirty) return;
    // Going private with "keep existing followers" off demotes every follower:
    // the same confirmation Settings → Privacy asks before that happens.
    if (
      changes.privateAccount === true &&
      user.keepFollowersOnPrivate === false &&
      !dropConfirmed
    ) {
      setConfirmDrop(true);
      return;
    }
    setSaving(true);
    const body = { ...changes };
    try {
      if (mediaDraft.avatarFile) {
        body.avatarImageKey = (await mediaService.upload('avatar', mediaDraft.avatarFile)).key;
      } else if (mediaDraft.clearAvatarImage) {
        body.avatarImageKey = '';
      }
      if (mediaDraft.bannerFile) {
        body.bannerImageKey = (await mediaService.upload('banner', mediaDraft.bannerFile)).key;
      } else if (mediaDraft.clearBannerImage) {
        body.bannerImageKey = '';
      }
    } catch (e) {
      setSaving(false);
      toast(uploadErrorMessage(e, 'Could not upload image. Please try again.'));
      return;
    }
    const ok = await updateProfile(body);
    setSaving(false);
    if (!ok) {
      toast('Could not save your profile. Please try again.');
      return;
    }
    toast('Profile saved');
    allowLeave.current = true;
    onSaved();
  }

  return (
    <>
      <Head>
        <div>
          <H1 id="edit-profile-title">Edit Profile</H1>
          <Lede>
            Update your information and customize how your profile appears to others on TruXSocial.
          </Lede>
        </div>
        <HeadActions>
          {dirty && <Unsaved role="status">Unsaved changes</Unsaved>}
          <Button variant="secondary" onClick={() => setPreviewing(true)}>
            <Icon name="eye" size={16} />
            Preview profile
          </Button>
          <Button
            variant="secondary"
            disabled={!dirty || saving}
            onClick={() => setConfirmDiscard(true)}
          >
            <Icon name="rotate-ccw" size={16} />
            Discard changes
          </Button>
          <Button disabled={!dirty || saving} onClick={() => void save()}>
            {saving ? 'Saving…' : 'Save changes'}
            {!saving && <Icon name="arrow-right" size={16} />}
          </Button>
        </HeadActions>
      </Head>

      <Grids>
        <TopGrid>
          <PhotoBannerCard state={state} errors={errors} user={user} />
          <BasicInfoCard state={state} errors={errors} user={user} />
          <AboutIdentityCard state={state} errors={errors} user={user} />
          <PreviewCard previewUser={previewUser} />
        </TopGrid>
        <BottomGrid>
          <LinksCard state={state} errors={errors} />
          <AppearanceCard state={state} />
          <PrivacyCard state={state} />
          <MottoCard />
          <InterestsCard state={state} />
          <VerificationCard user={user} />
        </BottomGrid>
      </Grids>

      {previewing && (
        <Dialog open title="Profile preview" onClose={() => setPreviewing(false)}>
          <ProfilePreview user={previewUser} large />
        </Dialog>
      )}
      {confirmDiscard && (
        <ConfirmDialog
          title="Discard your changes?"
          message="Everything you changed since your last save goes back to how it was."
          confirmLabel="Discard"
          onConfirm={() => {
            setConfirmDiscard(false);
            setErrors({});
            reset();
          }}
          onClose={() => setConfirmDiscard(false)}
        />
      )}
      {confirmDrop && (
        <ConfirmDropFollowersDialog
          onClose={() => setConfirmDrop(false)}
          onConfirm={() => {
            setConfirmDrop(false);
            void save(true);
          }}
        />
      )}
      {blocker.state === 'blocked' && (
        <ConfirmDialog
          title="Leave without saving?"
          message="Your changes to your profile have not been saved."
          confirmLabel="Leave"
          onConfirm={() => blocker.proceed()}
          onClose={() => blocker.reset()}
        />
      )}
    </>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export function EditProfilePage() {
  const { currentUser, isAuthenticated } = useSession();
  const location = useLocation();
  const [formKey, setFormKey] = useState(0);
  const [active, setActive] = useState(() => location.hash.replace('#', '') || 'photo');
  usePageTitle('Edit profile / TruXSocial');

  const jump = useCallback((id: string) => {
    setActive(id);
    const el = document.getElementById(id);
    el?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
    window.history.replaceState(window.history.state, '', `#${id}`);
    // Move focus to the card so keyboard and screen reader users land there too.
    const heading = el?.querySelector<HTMLElement>('h3');
    if (heading) {
      heading.tabIndex = -1;
      heading.focus({ preventScroll: true });
    }
  }, []);

  // Arriving from a link such as /settings/profile#photo lands on that card.
  useEffect(() => {
    const id = location.hash.replace('#', '');
    if (id) document.getElementById(id)?.scrollIntoView?.({ block: 'start' });
  }, [location.hash]);

  // The sub-nav follows the scroll: the card nearest the top of the view is active.
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (visible) setActive(visible.target.id);
      },
      { rootMargin: '-90px 0px -55% 0px' },
    );
    for (const s of EDIT_SECTIONS) {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
  }, [formKey]);

  if (!isAuthenticated || !currentUser) {
    return <Navigate to={`/signin?next=${encodeURIComponent(location.pathname)}`} replace />;
  }

  return (
    <Page>
      <SettingsHeader />
      <Body>
        <SidebarWrap>
          <EditProfileSidebar active={active} onJump={jump} />
        </SidebarWrap>
        <Main id="edit-profile-main" aria-labelledby="edit-profile-title" tabIndex={-1}>
          <MobileNav aria-label="Edit profile sections">
            <Link to="/settings/account">
              <Icon name="arrow-left" size={12} /> Settings
            </Link>
            {EDIT_SECTIONS.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                aria-current={s.id === active ? 'location' : undefined}
                onClick={(e) => {
                  e.preventDefault();
                  jump(s.id);
                }}
              >
                {s.label}
              </a>
            ))}
          </MobileNav>
          <EditProfileForm
            key={formKey}
            user={currentUser}
            onSaved={() => setFormKey((k) => k + 1)}
          />
        </Main>
      </Body>
    </Page>
  );
}
